import React, { useCallback, useState } from 'react';

import { useDispatch } from 'react-redux';

import { updateProfile } from '@techstyle/react-accounts';
import { FormattedMessage } from '@techstyle/react-intl';
import { useSession } from '@techstyle/redux-core';

import {
  calculateBraSize,
  getEquivalentWirelessSize,
  Unit,
} from '../../utils/braCalculator';
import { formatProfileData } from '../../utils/formatProfileData';
import { useToastMessage } from '../CartDrawer/useToastMessage';

import BraCalculatorForm from './BraCalculatorForm';
import BraCalculatorTabs from './BraCalculatorTabs';
import HowToMeasure from './HowToMeasure';
import ReturnInfo from './ReturnInfo';
import SizeChart from './SizeChart';
import SizeResult from './SizeResult';

const BraCalculator = ({
  activeTabFromProps = 'calculator',
  reverseTabs = false,
  handleShopMySize = () => {},
}) => {
  const { showToastMessage } = useToastMessage();
  const [activeTab, setActiveTab] = useState(activeTabFromProps);
  const [showSizeResult, setShowSizeResult] = useState(false);
  const [hasUnderbustError, setHasUnderbustError] = useState(false);
  const [hasOverbustError, setHasOverbustError] = useState(false);
  const [calculatedSize, setCalculatedSize] = useState('');
  const [underbust, setUnderbust] = useState('');
  const [overbust, setOverbust] = useState('');
  const [equivalentSize, setEquivalentSize] = useState('');
  const [unit, setUnit] = useState<Unit>('inches');

  const { isLoggedIn } = useSession();
  const dispatch = useDispatch();

  const numRegex = /^\d*\.?\d*$/;

  const handleMeasurementChange = (value, setMeasurement, setError) => {
    setMeasurement(value);
    setError(!numRegex.test(value));
  };

  const saveMySize = useCallback(async () => {
    const profileData = {
      braSize: calculatedSize,
      braletteSize: getEquivalentWirelessSize(calculatedSize),
    };

    try {
      await dispatch(updateProfile(formatProfileData(profileData)));
      showToastMessage(
        <FormattedMessage
          id="fit_guide.size_saved_success"
          defaultMessage="Size was saved to your profile."
        />
      );
    } catch (error) {
      showToastMessage(
        <FormattedMessage
          id="global_checkout.general_error"
          defaultMessage="Oops we’ve encountered an error. Try again"
        />,
        4000,
        true
      );
    }
  }, [calculatedSize, dispatch, showToastMessage]);

  const handleUnderbustChange = event => {
    handleMeasurementChange(
      event.target.value,
      setUnderbust,
      setHasUnderbustError
    );
  };

  const handleOverbustChange = event => {
    handleMeasurementChange(
      event.target.value,
      setOverbust,
      setHasOverbustError
    );
  };

  const handleUnitChange = (newUnit: Unit) => {
    if (newUnit !== unit) {
      setUnit(newUnit);
      setUnderbust('');
      setOverbust('');
      setHasUnderbustError(false);
      setHasOverbustError(false);
    }
  };

  const handleFindMySize = () => {
    const size = calculateBraSize(
      parseFloat(underbust),
      parseFloat(overbust),
      unit
    );

    if (size) {
      setCalculatedSize(size);
      setEquivalentSize(getEquivalentWirelessSize(size));
      setShowSizeResult(true);
    } else {
      setCalculatedSize('');
      setEquivalentSize('');
      setShowSizeResult(true);
    }
  };

  return (
    <>
      <BraCalculatorTabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        reverseTabs={reverseTabs}
      />

      {activeTab === 'calculator' && (
        <>
          <BraCalculatorForm
            unit={unit}
            underbust={underbust}
            overbust={overbust}
            hasUnderbustError={hasUnderbustError}
            hasOverbustError={hasOverbustError}
            showSizeResult={showSizeResult}
            handleUnitChange={handleUnitChange}
            handleUnderbustChange={handleUnderbustChange}
            handleOverbustChange={handleOverbustChange}
            handleFindMySize={handleFindMySize}
          />

          {showSizeResult && (
            <SizeResult
              calculatedSize={calculatedSize}
              equivalentSize={equivalentSize}
              saveMySize={saveMySize}
              isLoggedIn={isLoggedIn}
              handleShopMySize={handleShopMySize}
            />
          )}

          <HowToMeasure />
          <ReturnInfo />
        </>
      )}

      {activeTab === 'sizeChart' && (
        <>
          <SizeChart />
          <HowToMeasure />
          <ReturnInfo />
        </>
      )}
    </>
  );
};

export default BraCalculator;
